import { create } from "zustand"
import { Platform } from "../types/Platforms"

export interface Product {
  platform: Platform;
  category: string;
  name: string;
  brand: string;
  articleId: string;
  src: string;
  link: string;
  matchScore: number;
  price: number;
  isKiloPrice: boolean;
  unitPrice: number;
  unitPriceUnit: string;
  quantity: number;
  useSingle: boolean;
  useCombo: boolean;
  standardUnit: string;
  standardizedAmount: number;
  estimatedWeightPerPiece: number;
  hasComboOption: boolean;
  combo?: ComboData;
  restaurantName?: string;  // sourcing: which holding restaurant this option was found at
}

export interface ComboData {
  price: number;
  multiplier: number;
  standardizedAmount: number;
}

export interface OptionsBundle {
sourcingOptions: Product[];
searchOptions: Product[];
}

export interface FileMetadata {
fileName: string;
startDate: string;
endDate: string;
}

export interface SelectedProductForImport {
  articleId: string;
  name: string
  quantity: number;
  platform: string;
}

export interface Report {
  id: string
  userId: string
  name: string
  startDate: string
  endDate: string
  sourcingData: unknown
  restaurantId: string
  createdAt: string
  updatedAt: string
}

export interface SourcingData {
  productsIdMap: Record<string, Product>
  optionsBundle: Record<string, OptionsBundle>
  selectedOptionIdMap: Record<string, Product>
}

type ReportsMap = Record<string, Report>

interface AppReportsState {
  reportsById: ReportsMap
  setReportsById: (reportsById: ReportsMap) => void

  loadReports: (reports: Report[]) => void
  addOrUpdateReport: (report: Report) => void
  removeReport: (id: string) => void

  getReport: (id: string) => Report | undefined
  getAllReportsArray: () => Report[]

  getProductsIdMap: (id: string) => Record<string, Product> | undefined
  setProductsIdMap: (id: string, map: Record<string, Product>) => void

  getOptionsBundle: (id: string) => Record<string, OptionsBundle> | undefined
  setOptionsBundle: (id: string, bundle: Record<string, OptionsBundle>) => void

  getSelectedOptionIdMap: (id: string) => Record<string, Product> | undefined
  updateSelectedOptionIdMap: (
    id: string,
    selected: Record<string, Product>
  ) => void
}

export const useAppReportsStore = create<AppReportsState>((set, get) => ({
  reportsById: {},

  setReportsById: (reportsById) => set({ reportsById }),

  loadReports: (list) => {
    const map: ReportsMap = {}
    for (const r of list) map[r.id] = r
    set({ reportsById: map })
  },

  addOrUpdateReport: (report) =>
    set((state) => ({
      reportsById: { ...state.reportsById, [report.id]: report },
    })),

  removeReport: (id) =>
    set((state) => {
      const { [id]: _discard, ...rest } = state.reportsById
      return { reportsById: rest }
    }),

  getReport: (id) => get().reportsById[id],
  getAllReportsArray: () => Object.values(get().reportsById),

  getProductsIdMap: (id) =>
    (get().reportsById[id]?.sourcingData as SourcingData | undefined)
      ?.productsIdMap,

  setProductsIdMap: (id, map) =>
    set((state) => {
      const report = state.reportsById[id]
      if (!report) return state
      const sd = (report.sourcingData as SourcingData) ?? ({} as SourcingData)
      const next: Report = {
        ...report,
        sourcingData: { ...sd, productsIdMap: map },
        updatedAt: new Date().toISOString(),
      }
      return { reportsById: { ...state.reportsById, [id]: next } }
    }),

  getOptionsBundle: (id) =>
    (get().reportsById[id]?.sourcingData as SourcingData | undefined)
      ?.optionsBundle,

  setOptionsBundle: (id, bundle) =>
    set((state) => {
      const report = state.reportsById[id]
      if (!report) return state
      const sd = (report.sourcingData as SourcingData) ?? ({} as SourcingData)
      const next: Report = {
        ...report,
        sourcingData: { ...sd, optionsBundle: bundle },
        updatedAt: new Date().toISOString(),
      }
      return { reportsById: { ...state.reportsById, [id]: next } }
    }),

  getSelectedOptionIdMap: (id) =>
    (get().reportsById[id]?.sourcingData as SourcingData | undefined)
      ?.selectedOptionIdMap,

  updateSelectedOptionIdMap: (id, selected) =>
    set((state) => {
      const report = state.reportsById[id]
      if (!report) return state
      const sd = (report.sourcingData as SourcingData) ?? ({} as SourcingData)
      const next: Report = {
        ...report,
        sourcingData: { ...sd, selectedOptionIdMap: selected },
        updatedAt: new Date().toISOString(),
      }
      return { reportsById: { ...state.reportsById, [id]: next } }
    }),
}))
